import { UserCapacity } from '@enums/user-capacity.enum';
import { DateHelper } from '@helpers/date.helper';
import { HttpHelper } from '@helpers/http.helper';
import { LegalPersonBase } from '@legal/legal-core/models/legal-person-base';
import { UserType } from '@modules/core/enums/user-type.enum';

export interface LegalPhysicalPersonJson {
  id: number;
  civility: string;
  firstname: string;
  lastname: string;
  director: boolean;
  beneficiary: boolean;
  capital_contribution: number;
  shares_number: number;
  shares_percentage: number;
  capacity: UserCapacity;
  email: string;
  phone: string;
  nationality?: string;
  birth_name?: string;
  birth_date?: string;
  birth_city?: string;
  birth_postal_code?: string;
  birth_city_insee_code?: string;
  birth_country?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  city_insee_code?: string;
  country?: string;
}

export class LegalPhysicalPerson extends LegalPersonBase {
  constructor(
    public id: number,
    public civility: string,
    public firstname: string,
    public lastname: string,
    public director: boolean,
    public beneficiary: boolean,
    public capitalContribution: number,
    public sharesNumber: number,
    public sharesPercentage: number,
    public capacity: UserCapacity,
    public email: string,
    public phone: string,
    public nationality?: string,
    public birthName?: string,
    public birthDate?: Date,
    public birthCity?: string,
    public birthPostalCode?: string,
    public birthCityInseeCode?: string,
    public birthCountry?: string,
    public address?: string,
    public city?: string,
    public postalCode?: string,
    public cityInseeCode?: string,
    public country?: string
  ) {
    super(id, capacity, director, beneficiary, capitalContribution, sharesNumber, sharesPercentage);

    this.type = UserType.user;
  }

  get fullName(): string {
    return `${this.firstname} ${this.lastname}`;
  }

  static fromJson(json: Partial<LegalPhysicalPersonJson>): LegalPhysicalPerson {
    return new LegalPhysicalPerson(
      json.id,
      json.civility,
      json.firstname,
      json.lastname,
      json.director,
      json.beneficiary,
      json.capital_contribution,
      json.shares_number,
      json.shares_percentage,
      json.capacity,
      json.email,
      json.phone,
      json.nationality,
      json.birth_name,
      json.birth_date ? DateHelper.toDate(json.birth_date) : undefined,
      json.birth_city,
      json.birth_postal_code,
      json.birth_city_insee_code,
      json.birth_country,
      json.address,
      json.city,
      json.postal_code,
      json.city_insee_code,
      json.country
    );
  }

  static toJson(person: LegalPhysicalPerson): Partial<LegalPhysicalPersonJson> {
    return {
      id: HttpHelper.toJsonProperty(person.id),
      civility: HttpHelper.toJsonProperty(person.civility),
      firstname: HttpHelper.toJsonProperty(person.firstname),
      lastname: HttpHelper.toJsonProperty(person.lastname),
      director: !!person.director,
      beneficiary: !!person.beneficiary,
      capital_contribution: HttpHelper.toJsonProperty(person.capitalContribution),
      shares_number: HttpHelper.toJsonProperty(person.sharesNumber),
      shares_percentage: HttpHelper.toJsonProperty(person.sharesPercentage),
      capacity: HttpHelper.toJsonProperty(person.capacity),
      email: HttpHelper.toJsonProperty(person.email),
      phone: HttpHelper.toJsonProperty(person.phone),
      nationality: HttpHelper.toJsonProperty(person.nationality),
      birth_name: HttpHelper.toJsonProperty(person.birthName),
      birth_date: person.birthDate ? DateHelper.format(person.birthDate) : undefined,
      birth_city: HttpHelper.toJsonProperty(person.birthCity),
      birth_postal_code: HttpHelper.toJsonProperty(person.birthPostalCode),
      birth_city_insee_code: HttpHelper.toJsonProperty(person.birthCityInseeCode),
      birth_country: HttpHelper.toJsonProperty(person.birthCountry),
      address: HttpHelper.toJsonProperty(person.address),
      city: HttpHelper.toJsonProperty(person.city),
      postal_code: HttpHelper.toJsonProperty(person.postalCode),
      city_insee_code: HttpHelper.toJsonProperty(person.cityInseeCode),
      country: HttpHelper.toJsonProperty(person.country)
    };
  }
}
